import { useEffect, useRef, useState, type FormEvent } from 'react';
import { AlertCircle, Check, Copy, Download, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/Button';
import { FormField } from '@/components/FormField';
import { Loading } from '@/components/Loading';
import { QrCode } from '@/components/QrCode';
import { TextField, type TextFieldProps } from '@/components/TextField';
import { Checkbox } from '@/components/Checkbox';
import { errorMessage } from '@/lib/api';
import { mfaApi, type MfaEnableResult, type MfaSetupResult } from './api';
import './security.css';

/** 確認コード（6 桁）の入力欄。全角数字・空白は自動で除去する */
export function MfaCodeInput({
  value,
  onChange,
  ...rest
}: Omit<TextFieldProps, 'value' | 'onChange'> & { value: string; onChange: (v: string) => void }) {
  return (
    <TextField
      className="bs-mfa-code"
      inputMode="numeric"
      autoComplete="one-time-code"
      maxLength={7}
      placeholder="123456"
      {...rest}
      value={value}
      onChange={(e) =>
        onChange(
          e.target.value
            .replace(/[０-９]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0))
            .replace(/\D/g, '')
            .slice(0, 6),
        )
      }
    />
  );
}

/** 秘密鍵を 4 文字ごとに区切って表示（手入力用） */
export function formatSecret(secret: string): string {
  return secret.replace(/(.{4})/g, '$1 ').trim();
}

/**
 * 認証アプリの登録（QR コード表示 → 確認コード入力 → 有効化）。
 * 有効化に成功すると onEnabled にリカバリーコード（1 回だけ表示）を渡す。
 */
export function MfaEnrollment({ onEnabled, onCancel }: { onEnabled: (r: MfaEnableResult) => void; onCancel?: () => void }) {
  const [setup, setSetup] = useState<MfaSetupResult | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    mfaApi
      .setup()
      .then(setSetup)
      .catch((e) => setLoadError(errorMessage(e)));
  }, []);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (code.length !== 6) {
      setError('認証アプリに表示されている 6 桁の数字を入力してください。');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      onEnabled(await mfaApi.enable(code));
    } catch (err) {
      setError(errorMessage(err));
      setCode('');
    } finally {
      setSubmitting(false);
    }
  };

  if (loadError) {
    return (
      <div className="bs-login__alert" role="alert">
        <AlertCircle size={18} aria-hidden />
        <span>{loadError}</span>
      </div>
    );
  }
  if (!setup) return <Loading block label="登録用の QR コードを作成しています" />;

  return (
    <form className="bs-mfa-enroll" onSubmit={onSubmit} noValidate>
      <ol className="bs-mfa-steps">
        <li>
          <span className="bs-mfa-steps__no">1</span>
          <div>
            <strong>認証アプリを用意します</strong>
            <p>スマートフォンに「Google Authenticator」または「Microsoft Authenticator」をインストールします。</p>
          </div>
        </li>
        <li>
          <span className="bs-mfa-steps__no">2</span>
          <div>
            <strong>QR コードを読み取ります</strong>
            <p>アプリで「＋」→「QR コードをスキャン」を選び、下の QR コードを読み取ります。</p>
            <div className="bs-mfa-qr">
              <QrCode value={setup.otpauthUri} size={184} label="認証アプリ登録用の QR コード" />
              <div className="bs-mfa-qr__manual">
                <span className="bs-mfa-qr__caption">読み取れない場合は手入力</span>
                <dl>
                  <dt>アカウント</dt>
                  <dd>
                    {setup.issuer}（{setup.accountName}）
                  </dd>
                  <dt>キー</dt>
                  <dd>
                    <code className="bs-mfa-secret" data-testid="mfa-secret">
                      {formatSecret(setup.secret)}
                    </code>
                  </dd>
                  <dt>種類</dt>
                  <dd>時間ベース（30 秒・6 桁）</dd>
                </dl>
              </div>
            </div>
          </div>
        </li>
        <li>
          <span className="bs-mfa-steps__no">3</span>
          <div>
            <strong>表示された 6 桁の確認コードを入力します</strong>
            <FormField label="確認コード" error={error ?? undefined}>
              <MfaCodeInput value={code} onChange={setCode} />
            </FormField>
          </div>
        </li>
      </ol>
      <div className="bs-mfa-actions">
        {onCancel && (
          <Button onClick={onCancel} disabled={submitting}>
            キャンセル
          </Button>
        )}
        <Button type="submit" variant="primary" icon={<ShieldCheck size={16} />} loading={submitting}>
          確認して有効にする
        </Button>
      </div>
    </form>
  );
}

/**
 * リカバリーコードの表示（1 回だけ）。コピー・テキスト保存と「保管しました」の確認を求める。
 */
export function RecoveryCodes({ codes, loginId, onDone, doneLabel = '次へ進む' }: { codes: string[]; loginId: string; onDone: () => void; doneLabel?: string }) {
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);
  const text = `外国漁船操業等調査・監視事業システム 2段階認証のリカバリーコード（${loginId}）\n各コードは 1 回だけ使用できます。\n\n${codes.join('\n')}\n`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };
  const download = () => {
    const url = URL.createObjectURL(new Blob([text], { type: 'text/plain;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `リカバリーコード_${loginId}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <section className="bs-recovery" aria-labelledby="recovery-title">
      <h2 id="recovery-title" className="bs-recovery__title">
        リカバリーコード
      </h2>
      <p className="bs-recovery__lead">
        スマートフォンの紛失・機種変更などで認証アプリを使えないときに、確認コードの代わりに入力します。
        <strong>この画面を閉じると二度と表示されません。</strong>印刷するか安全な場所に保管してください。
      </p>
      <ol className="bs-recovery__codes" data-testid="recovery-codes">
        {codes.map((c) => (
          <li key={c}>
            <code>{c}</code>
          </li>
        ))}
      </ol>
      <div className="bs-recovery__tools">
        <Button size="sm" icon={copied ? <Check size={14} /> : <Copy size={14} />} onClick={() => void copy()}>
          {copied ? 'コピーしました' : 'コピー'}
        </Button>
        <Button size="sm" icon={<Download size={14} />} onClick={download}>
          テキストで保存
        </Button>
      </div>
      <Checkbox label="リカバリーコードを保管しました（各コードは 1 回だけ使えます）" checked={saved} onChange={(e) => setSaved(e.target.checked)} />
      <div className="bs-mfa-actions">
        <Button variant="primary" disabled={!saved} onClick={onDone}>
          {doneLabel}
        </Button>
      </div>
    </section>
  );
}
